import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  actionUrl?: string;
  // The API has always returned these; they were just dropped by this type. They identify the
  // record a notification is about (e.g. entityType 'ShiftChangeRequest'), which is what the
  // module-scoped notification lists and the deep links key off.
  entityType?: string;
  entityId?: string;
  // Type-specific render payload the API already returns (a birthday row carries the celebrant's
  // name, designation and whether a photo exists, so the bell needs no extra request).
  data?: Record<string, any>;
}

interface NotificationsState {
  items: Notification[];
  unreadCount: number;
  isLoading: boolean;
  /** True once the unread count has come from the SERVER at least once — the toast effect
   *  baselines on that first real value instead of the mount default of 0. */
  unreadInitialized: boolean;
}

export const fetchNotificationsAsync = createAsyncThunk('notifications/fetch', async () => {
  // Unread only — the bell is a "still to look at" list, so it must not surface notifications
  // already read, and this keeps it in agreement with the unread-count badge.
  const response = await api.get('/notifications?limit=20&unread=true');
  return response.data;
});

export const fetchUnreadCountAsync = createAsyncThunk('notifications/unreadCount', async () => {
  const response = await api.get('/notifications/unread-count');
  return response.data.data.count;
});

export const markReadAsync = createAsyncThunk('notifications/markRead', async (ids: string[]) => {
  await api.put('/notifications/mark-read', { ids });
  return ids;
});

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState: { items: [], unreadCount: 0, isLoading: false, unreadInitialized: false } as NotificationsState,
  reducers: {
    addNotification: (state, action: PayloadAction<Notification>) => {
      state.items.unshift(action.payload);
      if (!action.payload.isRead) state.unreadCount++;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationsAsync.fulfilled, (state, action) => {
        state.items = action.payload.data || [];
      })
      .addCase(fetchUnreadCountAsync.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
        state.unreadInitialized = true;
      })
      .addCase(markReadAsync.fulfilled, (state, action) => {
        action.payload.forEach((id) => {
          const notif = state.items.find((n) => n.id === id);
          if (notif && !notif.isRead) {
            notif.isRead = true;
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          }
        });
      });
  },
});

export const { addNotification } = notificationsSlice.actions;
export default notificationsSlice.reducer;
